import http from "@/composables/axios";
import { defineStore, storeToRefs } from "pinia";
import { useAuthStore } from "./auth";
import { openDB } from 'idb';
import { markRaw } from "vue";

let casinoFetchPromise = null;

export const useGamesStore = defineStore('games', {
    state: () => ({
        casino: {
            categories: [],
            subcategories: [],
            providers: [],
            games: [],
        },
        categories: [],
        subcategories: [],
        games: [],
        recents:[],
        meta: {
            page: 1,
        },
        game: undefined,
        url: false,
        iframe: false,
        isGameFullscreen: false,
    }),

    getters: {
        getCasino: state => state.casino,
        getCategories: state => {
            const categories = [];
            return [...categories, ...state.casino.categories];
        },
        getCategory: state => categoryId => {
            return state.casino.categories.find(category => category._id == categoryId);
        },
        getSubcategories: state => categoryId => state.casino.subcategories.filter(s => s.cid == categoryId),
        getGames: state => (categoryId, subcategoryId, selectedProviders = []) => {
            return state.casino.games.filter(g => {
                const categoryMatch = g.cid == categoryId;
                const subcategoryMatch = g.subcategories.includes(subcategoryId);
                const providerMatch = selectedProviders.length == 0 || selectedProviders.includes(g.pid);
                return categoryMatch && subcategoryMatch && providerMatch;
            });
        },
        getProvidersByCategory: state => categoryId => state.casino.providers.filter(p => p.cid == categoryId),
        getAllProviders: state => state.casino.providers,
        getMeta: state => state.meta,
        getGame: state => state.game,
        isUrl: state => state.url,
        getNewGames: state => state.casino.games.filter(g => g.new == true),
        getRecents: state => {
            if (state.recents && state.recents.length > 0) {
                const recentSet = new Set(state.recents);
                return state.casino.games.filter(game => recentSet.has(game._id));
            }
            return [];
        },
        getIsGameFullscreen: state => state.isGameFullscreen,
        getProvidersWithoutLive: (state) => {
            return state.casino.providers.filter(p => {
                const category = state.casino.categories.find(c => c._id === p.cid)
                return category && category.nm !== 'Live Casino'
            })
        }
    },

    actions: {
        async fetchAllCasinoGames() {
            if (casinoFetchPromise) return casinoFetchPromise;

            casinoFetchPromise = (async () => {
                window.localStorage.removeItem('casino')
                window.localStorage.removeItem('casinoVersion')

                const forceFresh = window.localStorage.getItem('casinoStale') === '1';
                if (forceFresh) {
                    window.localStorage.removeItem('casinoStale');
                }

                const db = await openDB('MyDatabase', 23, {
                    upgrade(db) {
                        if (!db.objectStoreNames.contains('betting')) {
                            db.createObjectStore('betting');
                        }
                    },
                });
                if (forceFresh) {
                    try { await db.delete('betting', 'casino'); } catch (e) { /* noop */ }
                    try { await db.delete('betting', 'casinoVersion'); } catch (e) { /* noop */ }
                }
                const casinoVersion = forceFresh ? undefined : await db.get('betting', 'casinoVersion');
                try {
                    const res = await http.get(`03/all?version=${casinoVersion}`);
                    if (res.status !== 204 && res.data.ok) {
                        await db.put('betting', res.data.data, 'casino');
                        await db.put('betting', res.data.data.version, 'casinoVersion');
                    }
                    const casinoData = await db.get('betting', 'casino');
                    if (casinoData) {
                        this.casino = markRaw(casinoData);
                    }
                } finally {
                    casinoFetchPromise = null;
                }
            })();

            return casinoFetchPromise;
        },
        async refreshCasino() {
            if (casinoFetchPromise) return casinoFetchPromise;
            window.localStorage.setItem('casinoStale', '1');
            await this.fetchAllCasinoGames();
        },
        async fetchCategories() {
            const auth = useAuthStore()
            const { getUser } = storeToRefs(auth)
            await http.get('03/games/categories').then(res => {
                if (res.data.ok) {
                    this.categories = []
                    for (let data of res.data.data) {
                        this.categories.push(data)
                    }
                }
            })
        },
        async fetchRecentsGames() {
            await http.get('03/games/recent').then(res => {
                if (res.data.ok) {
                    this.recents = res.data.data.recent_games_fid
                }
            })
        }, 

        async searchGames(name) {
            return await http.get(`03/games/search?name=${name}`)
        },

        async fetchSubcategories(id) {
            this.games = []
            this.subcategories = []
            if (!id) return
            await http.get(`03/games/categories/${id}/subcategories`).then(res => {
                if (res.data.ok) {
                    this.subcategories = res.data.data
                }
            })
        },

        async fetchGames(categoryId, subcategoryId, more = false) {
            if (more) {
                this.meta.page++
            }
            await http.get(`03/games/categories/${categoryId}/subcategories/${subcategoryId}/resources?page=${this.meta.page}`).then(res => {
                if (res.data.ok) {
                    this.games = [...this.games, ...res.data.data.games]
                    this.meta = res.data.data.meta
                }
            })
        },

        async fetchMainGames() {
            return await http.get(`03/games/main`)
        },

        async fetchGamesByProvider(id, page = 1, search = '') {
            return await http.get(`03/games/providers/${id}/games?page=${page}&search=${search}`)
        },

        async openGame(id, payload) {
            return await http.post(`03/games/${id}/open`, payload).then(res => {
                if (res.data.ok) {
                    this.game = res.data.data

                    this.iframe = res.data.iframe
                    this.url = res.data.url
                }

                return res
            })
        },

        setGameFullscreen(isFullscreen) {
            this.isGameFullscreen = isFullscreen
        },
        setGameUrl(url) {
            this.game = url;
             this.url = true;
        },
    }
})
